<template>
	<div class="reservation-edit">
		<ModalHeader :title="getString('edit_reservation')" :button-text="getString('save')" :button-loading="roomsOrdersStore.editReservationLoading" @click-button="saveEdits" />
		<AppList :items="items" >
			<template v-slot:date-edit="{item}">
				<div class="list-item-header">
					<p class="list-item-title">{{ item.title }}</p>
					<DatePicker ref="date-picker-date" v-model="dateEdit" mode="date" color="teal" :min-date='now' :max-date="maxDate" :is-dark="userStore.darkMode" :disabled-dates="disabledDates" @update:modelValue="changedDate">
						<template #default="{ togglePopover }">
							<AppTextInput class="text-input ml-auto" @click="togglePopover()" readonly :placeholder="getString('reservation_date')"
										  :value="dateEdit ? formatDateOnly(dateEdit, false) : null"/>
						</template>
					</DatePicker>
				</div>
			</template>
			<template v-slot:time-edit="{item}">
				<div class="list-item-header">
					<p class="list-item-title">{{ item.title }}</p>
					<AppButton v-if="dailyTimeslots.length > 0" class="ml-auto" outline :text="timeEdit ? formatTimeHHmm(timeEdit, false) : getString('time')" :loading="roomsOrdersStore.editReservationLoading" @click="openTimeSelector" :disabled="dateEdit === null" />
					<p v-else class="no-time-available">{{ getString('no_time_available') }}</p>
				</div>
			</template>
			<template #guests-slot="{item}">
				<div class="list-item-header">
					<p class="list-item-title">{{ item.title }}</p>
					<div class="touch-number-input-container">
						<TouchNumberInput :min="1" :value="item.value" @changed-value="v => { item.callbackNumber(v) }" />
					</div>
				</div>
			</template>
			<template #list-slot="{item}">
				<AppList :items="item.subitems">
					<template #dogs-slot="{item}">
						<div class="list-item-header">
							<p class="list-item-title">{{ item.title }}</p>
							<div class="touch-number-input-container">
								<TouchNumberInput :min="0" :value="item.value" @changed-value="v => { item.callbackNumber(v) }" />
							</div>
						</div>
					</template>
					<template #children-slot="{item}">
						<div class="list-item-header">
							<p class="list-item-title">{{ item.title }}</p>
							<div class="touch-number-input-container">
								<TouchNumberInput :min="0" :value="item.value" @changed-value="v => { item.callbackNumber(v) }" />
							</div>
						</div>
					</template>
					<template #list-slot="{item}">
						<AppList :items="item.subitems"/>
					</template>
				</AppList>
			</template>
		</AppList>
		<ReservationTablesSelect :experience-id="reservation ? reservation.experience_id : null" :active="roomsOrdersStore.editReservationTables" :initial-selected-tables="initialSelectedTables" :reservation-people="reservationPeople" :reservation-date="reservationDate" :reservation-time="reservationTime" :reservation-duration="reservationDuration" @toggling-popup="togglingTables" @confirm-changes="changedTables" />
		<TimeSelector :active="timeSheetActive" :daily-timeslots="dailyTimeslots" :exceptions="scheduleExceptions" :availability="availability"
					  :max-capacity="maxCapacity" :selected-date="dateEdit" @toggling-popup="v => timeSheetActive = v"
					  @selected-time="setSelectedTime"/>
	</div>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import AppList from "@/shared/components/AppList.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppButton from "@/shared/components/AppButton.vue";
import TimeSelector from "@/console-new/components/ops/tables/TimeSelector.vue";
import ReservationTablesSelect from "@/console-new/components/ops/tables/ReservationTablesSelect.vue";
import TouchNumberInput from "@/console-new/components/shared/TouchNumberInput.vue";
import {useUserStore} from "@/user/stores";
import {formatDateTime, formatDateOnly, formatTimeHHmm, getLocalDateFromServer, localToServerTime} from "@/shared/helpers/dates";
import {buildScheduleDisabledDates} from "@/shared/helpers/experiences";
import {validateEmail, validatePhone} from "@/shared/helpers/strings";
import {useExperiencesStore, useRoomsOrdersStore, useRoomsStore, useOrdersStore} from "@/console-new/stores";
import API from "@/shared/helpers/API";
import {timeslotTimes} from "@/book/helpers/availabilities";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const roomsOrdersStore = useRoomsOrdersStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const roomsStore = useRoomsStore();
		const ordersStore = useOrdersStore();
		const experiencesStore = useExperiencesStore();
		return {
			getString: languageStore.getString,
			roomsOrdersStore,
			userStore,
			notificationStore,
			roomsStore,
			ordersStore,
			experiencesStore,
			formatDateTime,
			formatDateOnly,
			formatTimeHHmm
		}
	},
	name: "ReservationEdit",
	components: {AppTextInput, AppButton, TimeSelector, AppList, ModalHeader, ReservationTablesSelect, TouchNumberInput},
	data() {
		return {
			now: new Date(),
			originalReservationSnapshot: null,
			dateEdit: null,
			timeEdit: null,
			occasions: [
				'birthday',
				'anniversary',
				'honeymoon',
				'business_reason',
				'graduation',
			],
			newName: null,
			newEmail: null,
			newPhone: null,
			newPeople: null,
			newDogs: null,
			newChildren: null,
			newNotes: null,
			newSpecial: null,
			newTables: null,
			timeSheetActive: false,
			scheduleTimeslots: [],
			scheduleExceptions: [],
			availability: [],
			bookedTimes: {},
			maxCapacity: 0,
		}
	},
	computed: {
		reservation() {
			if(!this.roomsOrdersStore.editReservationId) {
				return null;
			}
			// Fall back to the snapshot captured on open so the edit context survives if the
			// reservation ever drops out of the store's day-list
			return this.roomsOrdersStore.getReservationById(this.roomsOrdersStore.editReservationId) || this.originalReservationSnapshot || null;
		},
		customerName() {
			return this.reservation ? this.reservation.name : "";
		},
		reservationEmail() {
			return this.reservation ? this.reservation.email : "";
		},
		reservationPhone() {
			return this.reservation ? this.reservation.phone : "";
		},
		reservationPeople() {
			return this.reservation && this.reservation.people ? Number(this.reservation.people) : 0;
		},
		reservationDogs() {
			return this.reservation && this.reservation.dogs ? Number(this.reservation.dogs) : 0;
		},
		reservationChildren() {
			return this.reservation && this.reservation.children ? Number(this.reservation.children) : 0;
		},
		reservationTables() {
			if(this.newTables) {
				return this.newTables;
			} else {
				return this.reservation && this.reservation.tables ? this.reservation.tables : [];
			}
		},
		tablesTitle() {
			if (!this.reservationTables || this.reservationTables.length <= 0) {
				return this.getString('select_tables');
			} else if(this.reservationTables.length === 1) {
				return "1 " + this.getString('table');
			} else {
				return this.reservationTables.length + " " + this.getString('tables');
			}
		},
		initialSelectedTables() {
			let selectedTables = {};
			this.reservationTables.forEach(t => {
				selectedTables[t] = true;
			});
			return selectedTables;
		},
		dailyTimeslots() {
			if (!this.dateEdit || !this.scheduleTimeslots || this.scheduleTimeslots.length === 0) {
				return [];
			}
			const dateStr = formatDateOnly(this.dateEdit);
			const foundException = this.scheduleExceptions.find(ex => formatDateOnly(new Date(ex.date)) === dateStr);
			if (foundException) {
				let times = [];
				(foundException.timeslots || []).forEach(t => {
					let tHours = timeslotTimes(t, this.availability, this.maxCapacity, this.isDateToday, this.bookedTimes);
					if (tHours.length > 0) {
						times.push({slot: t, times: tHours});
					}
				});
				return times;
			}
			const weekday = this.dateEdit.getDay();
			let timeslots = this.scheduleTimeslots.filter(t => ((t.weekday + 1) % 7) === weekday);

			let times = [];
			timeslots.forEach(t => {
				let tHours = timeslotTimes(t, this.availability, this.maxCapacity, this.isDateToday, this.bookedTimes);
				if(tHours.length > 0) {
					times.push({slot: t, times: tHours});
				}
			});
			return times;
		},
		isDateToday() {
			return this.dateEdit && formatDateOnly(new Date()) === formatDateOnly(this.dateEdit);
		},
		maxDate() {
			let maxDate = new Date();
			const currentExperience = this.reservation ? this.experiencesStore.getExperienceById(Number(this.reservation.experience_id)) : null;
			maxDate.setDate(maxDate.getDate() + (currentExperience && currentExperience.days_advance ? currentExperience.days_advance : 30));
			return maxDate;
		},
		disabledDates() {
			const currentExperience = this.reservation
				? this.experiencesStore.getExperienceById(Number(this.reservation.experience_id))
				: null;
			const daysAdvance = currentExperience?.days_advance || 30;
			return buildScheduleDisabledDates(this.scheduleExceptions, this.scheduleTimeslots, daysAdvance);
		},
		items() {
			return [
				{
					title: this.getString('reservation_date'),
					customSlot: 'date-edit',
				},
				{
					title: this.getString('time'),
					customSlot: 'time-edit',
				},
				{
					title: this.getString('guests'),
					customSlot: 'guests-slot',
					value: this.reservationPeople,
					callbackNumber: (val) => {
						this.newPeople = val;
					},
				},
				{
					title: this.getString('guest_name'),
					textInput: true,
					placeholder: this.getString('guest_name'),
					value: this.customerName,
					callbackText: (val) => {
						this.newName = val;
					},
				},
				{
					title: this.getString('email'),
					textInput: true,
					placeholder: this.getString('email'),
					type: 'email',
					value: this.reservationEmail,
					callbackText: (val) => {
						this.newEmail = val;
					},
				},
				{
					title: this.getString('phone'),
					textInput: true,
					placeholder: this.getString('phone'),
					type: 'tel',
					value: this.reservationPhone,
					callbackText: (val) => {
						this.newPhone = val;
					},
				},
				{
					title: this.getString('tables'),
					buttonTitle: this.tablesTitle,
					buttonDisabled: !this.timeEdit,
					callbackButton: () => {
						this.roomsOrdersStore.editReservationTables = true;
					},
				},
				{
					title: this.getString('other'),
					dropdownSlot: 'list-slot',
					chevronItem: true,
					subitems: [
						{
							title: this.getString('dogs'),
							customSlot: 'dogs-slot',
							value: this.reservationDogs,
							callbackNumber: (val) => {
								this.newDogs = val;
							},
						},
						{
							title: this.getString('children'),
							customSlot: 'children-slot',
							value: this.reservationChildren,
							callbackNumber: (val) => {
								this.newChildren = val;
							},
						},
						{
							title: this.getString('special_occasion'),
							dropdownSlot: 'list-slot',
							switch: true,
							active: this.reservation && this.reservation.special,
							callback: (val) => {
								if (val) {
									this.newSpecial = this.occasions[0];
								} else {
									this.newSpecial = null;
								}
							},
							subitems: [
								{
									title: this.getString('special_occasion'),
									selectValues: this.occasions.map(o => this.getString(o)),
									selectedIndex: this.specialIndex,
									callbackSelect: (index) => {
										this.newSpecial = this.occasions[index];
									},
								}
							]
						},
						{
							title: this.getString('notes'),
							textInput: true,
							isTextArea: true,
							placeholder: this.getString('notes'),
							value: this.reservation ? this.reservation.notes : "",
							callbackText: (val) => {
								this.newNotes = val;
							},
						},
					]
				},
			]
		},
		specialIndex() {
			let index = this.occasions.indexOf(this.reservation && this.reservation.special ? this.reservation.special : '');
			return index > -1 ? index : 0;
		},
		reservationDate() {
			if(this.dateEdit) {
				return new Date(this.dateEdit);
			}
			return null;
		},
		reservationTime() {
			if(this.timeEdit) {
				return new Date(this.timeEdit);
			}
			return null;
		},
		originalTimeHHmm() {
			if(!this.reservation?.fullDate) return null;
			return formatTimeHHmm(this.reservation.fullDate, false);
		},
		reservationDuration() {
			if(!this.timeEdit) return 60;
			let tStr = formatTimeHHmm(this.timeEdit, false);
			for(let entry of this.dailyTimeslots) {
				if(entry.times.find(tt => tt.time === tStr)) {
					return entry.slot.time || 60;
				}
			}
			return 60;
		}
	},
	methods: {
		changedTables(selectedTables) {
			if(selectedTables && selectedTables.length > 0) {
				this.newTables = selectedTables;
			}
		},
		togglingTables(v) {
			if(!v) {
				this.roomsOrdersStore.editReservationTables = false;
			}
		},
		initEditFields() {
			const id = this.roomsOrdersStore.editReservationId;
			const stored = id ? this.roomsOrdersStore.getReservationById(id) : null;
			if(stored && (!this.originalReservationSnapshot || this.originalReservationSnapshot.reservation_id !== stored.reservation_id)) {
				this.originalReservationSnapshot = stored;
			}
			if(this.reservation && this.reservation.fullDate) {
				this.dateEdit = new Date(this.reservation.fullDate);
				this.timeEdit = new Date(this.reservation.fullDate);
				this.loadExperienceData();
			}
		},
		checkDateAvailable() {
			if (!this.dateEdit) return;

			const selectedDate = new Date(this.dateEdit);
			const selDay = selectedDate.getDay() + 1;

			const isDateDisabled = this.disabledDates.some(disabledEntry => {
				if (disabledEntry instanceof Date) {
					return selectedDate.toDateString() === disabledEntry.toDateString();
				}

				if (disabledEntry.repeat && Array.isArray(disabledEntry.repeat.weekdays)) {
					return disabledEntry.repeat.weekdays.includes(selDay);
				}

				return false;
			});

			if (isDateDisabled) {
				this.dateEdit = null;
			}
		},
		changedDate() {
			if(this.timeEdit) {
				this.timeEdit = null;
			}
		},
		openTimeSelector() {
			this.timeSheetActive = true;
		},
		setSelectedTime(time) {
			if (!time) return;
			const parts = time.split(":");
			if (parts.length >= 2) {
				const d = new Date(this.dateEdit);
				d.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
				this.timeEdit = d;
			}
		},
		loadExperienceData() {
			if (!this.reservation || !this.reservation.experience_id) {
				this.scheduleTimeslots = [];
				this.scheduleExceptions = [];
				return;
			}

			const schedulePromise = new Promise((resolve, reject) => {
				API.init().getExperienceSchedule(
					this.userStore.currBusinessId,
					this.reservation.experience_id,
					res => resolve(res),
					err => reject(err)
				);
			});

			const exceptionsPromise = new Promise((resolve, reject) => {
				API.init().getExperienceExceptions(
					this.userStore.currBusinessId,
					this.reservation.experience_id,
					res => resolve(res),
					err => reject(err)
				);
			});

			this.roomsOrdersStore.editReservationLoading = true;
			Promise.all([schedulePromise, exceptionsPromise])
				.then(([scheduleRes, exceptionsRes]) => {

					this.roomsOrdersStore.editReservationLoading = false;

					this.scheduleTimeslots = scheduleRes?.timeslots
						? scheduleRes.timeslots.map(t =>
							this.experiencesStore.normalizeTimeslot(t)
						)
						: [];

					if (exceptionsRes?.exceptions) {
						const exMap = {};
						exceptionsRes.exceptions_timeslots.forEach(t => {
							if (!t.experience_timeslot_exception_id) return;
							const id = t.experience_timeslot_exception_id;
							(exMap[id] ??= []).push(
								this.experiencesStore.normalizeTimeslot(t)
							);
						});
						this.scheduleExceptions = exceptionsRes.exceptions.map(e => ({
							experience_timeslot_exception_id: Number(
								e.experience_timeslot_exception_id
							),
							date: e.date,
							timeslots: exMap[e.experience_timeslot_exception_id] ?? []
						}));
					} else {
						this.scheduleExceptions = [];
					}

					if(this.dateEdit) {
						this.checkDateAvailable();
					}
				})
				.catch(err => {
					console.error('Failed to load experience data:', err);
					this.scheduleTimeslots = [];
					this.scheduleExceptions = [];
					this.roomsOrdersStore.editReservationLoading = false;
				});
		},
		loadAvailability() {
			if (!this.reservation || !this.reservation.experience_id || !this.dateEdit) {
				this.availability = [];
				this.bookedTimes = {};
				return;
			}
			let date = formatDateOnly(this.dateEdit);
			API.init().getExperienceAvailability(this.userStore.currBusinessId, this.reservation.experience_id, date, r => {
				if (r && r.result === 'OK') {

					r.availability.forEach(reservation => {
						let start = getLocalDateFromServer(date + " " + reservation.start);
						let end = getLocalDateFromServer(date + " " + reservation.end);
						reservation.start = formatTimeHHmm(start, false);
						reservation.end = formatTimeHHmm(end, false);
					});
					let bookedTimes = {};
					Object.entries(r.booked_times || {}).forEach(([time, people]) => {
						let localTime = getLocalDateFromServer(date + " " + time);
						bookedTimes[formatTimeHHmm(localTime, false)] = people;
					});
					this.availability = r.availability;
					this.bookedTimes = bookedTimes;
					this.maxCapacity = r.max_capacity;
				} else {
					this.availability = [];
					this.bookedTimes = {};
					this.maxCapacity = 0;
				}
				this.preselectOriginalTime();
			});
		},
		preselectOriginalTime() {
			if(this.timeEdit || !this.originalTimeHHmm) return;
			const available = this.dailyTimeslots.some(entry =>
				entry.times.some(tt => tt.time === this.originalTimeHHmm && tt.available)
			);
			if(available) {
				this.setSelectedTime(this.originalTimeHHmm);
			}
		},
		saveEdits() {
			this.roomsOrdersStore.editReservationLoading = true;

			let edits = {};
			if(this.newName && this.newName !== this.customerName) {
				edits["name"] = this.newName;
			}
			if(this.newEmail && this.newEmail !== this.reservationEmail) {
				if(!validateEmail(this.newEmail)) {
					this.roomsOrdersStore.editReservationLoading = false;
					this.notificationStore.showNotification("invalid-email");
					return;
				}
				edits["email"] = this.newEmail;
			}
			if(this.newPhone && this.newPhone !== this.reservationPhone) {
				if(!validatePhone(this.newPhone)) {
					this.roomsOrdersStore.editReservationLoading = false;
					this.notificationStore.showNotification("invalid-phone");
					return;
				}
				edits["phone"] = this.newPhone;
			}
			if(this.newPeople && this.newPeople !== this.reservationPeople) {
				edits["people"] = this.newPeople;
			}
			if(this.newDogs && this.newDogs !== this.reservationDogs) {
				edits["dogs"] = this.newDogs;
			}
			if(this.newChildren && this.newChildren !== this.reservationChildren) {
				edits["children"] = this.newChildren;
			}
			if(this.newNotes && this.newNotes !== this.reservation.notes) {
				edits["notes"] = this.newNotes;
			}
			if(this.newSpecial && this.newSpecial !== this.reservation.special) {
				edits["special"] = this.newSpecial;
			}
			const dateChanged = this.dateEdit && this.reservation?.date !== formatDateOnly(this.dateEdit);
			if(dateChanged && !this.timeEdit) {
				// Changing the day resets the time; the new day may not offer the original slot,
				// so the user has to pick a valid one before we can save the moved reservation.
				this.roomsOrdersStore.editReservationLoading = false;
				this.notificationStore.showNotification("select-valid-time");
				return;
			}
			if(this.dateEdit && this.timeEdit) {
				let combined = new Date(this.dateEdit);
				combined.setHours(this.timeEdit.getHours(), this.timeEdit.getMinutes(), 0, 0);
				let dateServer = localToServerTime(combined);
				let newDateStr = dateServer.toISOString().split('T')[0];
				let newTimeStr = dateServer.toTimeString().split(' ')[0];

				if(this.reservation.date !== newDateStr) {
					edits["date"] = newDateStr;
				}
				if(this.reservation.time !== newTimeStr) {
					edits["time"] = newTimeStr;
				}
			}
			if(this.newTables && this.newTables.length > 0) {
				edits["tables"] = this.newTables;
			}
			if(Object.keys(edits).length === 0) {
				this.roomsOrdersStore.editReservationLoading = false;
				this.notificationStore.showNotification("nothing-to-save");
				return;
			}


			API.init().updateReservation(this.userStore.currBusinessId,
				this.reservation.reservation_id,
				edits, (r) => {
				this.roomsOrdersStore.editReservationLoading = false;
				if(r && r.result && r.result === "OK") {

					if(edits["name"]) {
						this.reservation.name = this.newName;
					}
					if(edits["email"]) {
						this.reservation.email = this.newEmail;
					}
					if(edits["phone"]) {
						this.reservation.phone = this.newPhone;
					}
					if(edits["people"]) {
						this.reservation.people = this.newPeople;
					}
					if(edits["dogs"]) {
						this.reservation.dogs = this.newDogs;
					}
					if(edits["children"]) {
						this.reservation.children = this.newChildren;
					}
					if(edits["notes"]) {
						this.reservation.notes = this.newNotes;
					}
					if(edits["special"]) {
						this.reservation.special = this.newSpecial;
					}
					let forceRefresh = false;
					if(edits["tables"]) {
						this.reservation.tables = this.newTables;
						this.reservation.roomsTables = this.roomsOrdersStore.getTablesRoomFromIds(this.reservation.tables);
						forceRefresh = true;
					}
					if(edits["date"]) {
						this.reservation.date = edits["date"];
						// Move the underlying /tables/reservations calendar to the new date so the
						// saved reservation stays in view (dateEdit is the local date the user picked).
						if(this.dateEdit) {
							this.roomsOrdersStore.reservationsSelectedDate = new Date(this.dateEdit);
						}
						forceRefresh = true;
					}
					if(edits["time"]) {
						this.reservation.time = edits["time"];
						forceRefresh = true;
					}
					if(forceRefresh) {
						this.roomsOrdersStore.manageReservationId = null;

						if(this.roomsOrdersStore.reservationsTablesActive) {
							this.roomsOrdersStore.getDayReservations(() => {
								this.notificationStore.showNotification("generic-success");
								this.ordersStore.tablesToCheck = true;
							});
						} else {
							this.roomsStore.updateRoomStatus(this.roomsStore.activeRoom, (r) => {
								this.notificationStore.showNotification("generic-success");
								this.ordersStore.tablesToCheck = true;
							});
						}

					} else {
							this.notificationStore.showNotification("generic-success");
							this.ordersStore.tablesToCheck = true;
					}

					this.roomsOrdersStore.editReservationId = null;

				} else {
					this.notificationStore.showNotification("generic-fail");
				}
			});
		},
	},
	mounted() {
		this.initEditFields();
	},
	beforeUnmount() {
		this.roomsOrdersStore.editReservationLoading = false;
	},
	watch: {
		// Re-init only when a different reservation is opened. Watching the reservation
		// object itself would re-fire when the computed falls back to the snapshot after a
		// date edit (same reservation, new reference), which would reset the user's date.
		"roomsOrdersStore.editReservationId"() {
			this.initEditFields();
		},
		dateEdit() {
			this.loadAvailability();
		}
	}
}
</script>

<style scoped lang="scss">
.list-item-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	width: 100%;

	.list-item-title {
		margin-right: 5px;
	}

	.input-wrapper {
		width: 240px;
		margin-left: auto;

		@media (max-width: 1250px) {
			width: 190px;
		}
	}

	.no-time-available {
		font-size: 16px;
		color: var(--color-text-primary);
		background-color: var(--color-bg-primary-2);
		border-radius: 13px;
		padding: 6px 10px;
		min-height: 35px;
		width: fit-content;
	}

	.touch-number-input-container {
		margin-left: auto;
		
		.input-wrapper {
			width: auto !important;
		}
	}

}
</style>